import { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { firestore } from "../../firebase";
import { IconUsers, IconMail, IconWorld, IconDeviceAnalytics } from "@tabler/icons-react";

type Visitor = {
	id: string;
	createdAt: string;
	address?: {
		country?: string;
	};
	visitCount?: number;
};

type Contact = {
	id: string;
	createdAt: string;
	name: string;
	email: string;
};

type DashboardStats = {
	totalVisitors: number;
	totalContacts: number;
	activeCountries: number;
	averageVisits: number;
	recentVisitors: Array<{
		id: string;
		createdAt: string;
		country?: string;
	}>;
	recentContacts: Array<{
		id: string;
		createdAt: string;
		name: string;
		email: string;
	}>;
};

const StatCard = ({
	title,
	value,
	icon: Icon,
	description,
}: {
	title: string;
	value: number | string;
	icon: any;
	description: string;
}) => (
	<div className='rounded-lg bg-white dark:bg-gray-800 p-6 shadow-sm ring-1 ring-gray-900/5 dark:ring-white/10'>
		<div className='flex items-center'>
			<div className='flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 dark:bg-primary/5'>
				<Icon className='h-6 w-6 text-primary' />
			</div>
			<div className='ml-4'>
				<h3 className='text-sm font-medium text-gray-900 dark:text-gray-100'>{title}</h3>
				<p className='mt-1 text-2xl font-semibold text-gray-900 dark:text-white'>{value}</p>
			</div>
		</div>
		<p className='mt-2 text-sm text-gray-500 dark:text-gray-400'>{description}</p>
	</div>
);

export default function DashboardPage() {
	const [stats, setStats] = useState<DashboardStats>({
		totalVisitors: 0,
		totalContacts: 0,
		activeCountries: 0,
		averageVisits: 0,
		recentVisitors: [],
		recentContacts: [],
	});
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchStats = async () => {
			try {
				// Fetch visitors
				const visitorsSnapshot = await getDocs(collection(firestore, "visiters"));
				const visitors = visitorsSnapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
				}));

				// Fetch contacts
				const contactsSnapshot = await getDocs(collection(firestore, "contactForm"));
				const contacts = contactsSnapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
				}));

				// Calculate stats
				const countries = new Set(visitors.map((visitor: any) => visitor.address?.country).filter(Boolean));
				const totalVisits = visitors.reduce((sum: number, visitor: any) => sum + (visitor.visitCount || 0), 0);

				// Get recent visitors
				const recentVisitorsQuery = query(collection(firestore, "visiters"), orderBy("createdAt", "desc"), limit(5));
				const recentVisitorsSnapshot = await getDocs(recentVisitorsQuery);
				const recentVisitors = recentVisitorsSnapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
				})) as Visitor[];

				// Get recent contacts
				const recentContactsQuery = query(collection(firestore, "contactForm"), orderBy("createdAt", "desc"), limit(5));
				const recentContactsSnapshot = await getDocs(recentContactsQuery);
				const recentContacts = recentContactsSnapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
				})) as Contact[];

				setStats({
					totalVisitors: visitors.length,
					totalContacts: contacts.length,
					activeCountries: countries.size,
					averageVisits: totalVisits / visitors.length || 0,
					recentVisitors,
					recentContacts,
				});
			} catch (error) {
				console.error("Error fetching stats:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchStats();
	}, []);

	if (loading) {
		return (
			<div className='flex h-[calc(100vh-4rem)] items-center justify-center'>
				<div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent'></div>
			</div>
		);
	}

	return (
		<div>
			<div className='mb-8'>
				<h1 className='text-2xl font-semibold text-gray-900 dark:text-white'>Dashboard</h1>
				<p className='mt-2 text-sm text-gray-700 dark:text-gray-400'>Overview of your website statistics</p>
			</div>

			<div className='grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4'>
				<StatCard
					title='Total Visitors'
					value={stats.totalVisitors}
					icon={IconUsers}
					description='Unique visitors to your website'
				/>
				<StatCard
					title='Contact Forms'
					value={stats.totalContacts}
					icon={IconMail}
					description='Total contact form submissions'
				/>
				<StatCard
					title='Active Countries'
					value={stats.activeCountries}
					icon={IconWorld}
					description='Countries with active visitors'
				/>
				<StatCard
					title='Average Visits'
					value={stats.averageVisits.toFixed(1)}
					icon={IconDeviceAnalytics}
					description='Average visits per visitor'
				/>
			</div>

			<div className='mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2'>
				{/* Recent Visitors */}
				<div className='rounded-lg bg-white dark:bg-gray-800 p-6 shadow-sm ring-1 ring-gray-900/5 dark:ring-white/10'>
					<h3 className='text-base font-semibold text-gray-900 dark:text-white'>Recent Visitors</h3>
					<div className='mt-6 flow-root'>
						<div className='-my-5 divide-y divide-gray-200 dark:divide-gray-800'>
							{stats.recentVisitors.map((visitor: any) => (
								<div key={visitor.id} className='py-5'>
									<div className='flex items-center justify-between'>
										<div>
											<p className='text-sm font-medium text-gray-900 dark:text-white'>
												{visitor.address?.country || "Unknown Location"}
											</p>
											<p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
												{new Date(visitor.createdAt).toLocaleDateString()}
											</p>
										</div>
										<div className='ml-4 flex-shrink-0'>
											<span className='inline-flex items-center rounded-full bg-green-50 dark:bg-green-500/10 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-400 ring-1 ring-inset ring-green-600/20 dark:ring-green-500/20'>
												New visitor
											</span>
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>

				{/* Recent Contacts */}
				<div className='rounded-lg bg-white dark:bg-gray-800 p-6 shadow-sm ring-1 ring-gray-900/5 dark:ring-white/10'>
					<h3 className='text-base font-semibold text-gray-900 dark:text-white'>Recent Contact Forms</h3>
					<div className='mt-6 flow-root'>
						<div className='-my-5 divide-y divide-gray-200 dark:divide-gray-800'>
							{stats.recentContacts.map((contact: any) => (
								<div key={contact.id} className='py-5'>
									<div className='flex items-center justify-between'>
										<div>
											<p className='text-sm font-medium text-gray-900 dark:text-white'>{contact.name}</p>
											<p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>{contact.email}</p>
										</div>
										<div className='ml-4 flex-shrink-0'>
											<p className='text-sm text-gray-500 dark:text-gray-400'>
												{new Date(contact.createdAt).toLocaleDateString()}
											</p>
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
